import { useQuery } from '@tanstack/react-query';
import { formatCount } from '@/format';
import { useState } from 'react';
import { api } from '@/api/client';
import type { components } from '@/api/schema';
import {
  DataGrid,
  DetailPane,
  SplitView,
  StatusBar,
  writeStored,
  type GridColumn,
} from '@/components/desktop';
import { AppIcon, type IconKind } from '@/components/AppIcon';
import { ErrorNote, shortDate } from '@/components/ui';
import { useAuth } from '@/stores/auth';
import { useFarmScope } from '@/stores/farmScope';
import { useShellNav } from '@/stores/shellNav';

type Farm = components['schemas']['FarmDto'];
type Shed = components['schemas']['ShedDto'];
type Flock = components['schemas']['FlockDto'];
type HierarchyFarm = components['schemas']['HierarchyFarmDto'];

/** One farm's figures, put together from the three lists the API gives. */
interface FarmSummary {
  id: string;
  code: string;
  nameEn: string;
  district: string | null;
  isActive: boolean;
  sheds: number;
  sections: number;
  pens: number;
  capacity: number;
  stores: number;
  flocks: number;
  /** Live flocks only, so a farm between cycles reads as empty, not stale. */
  liveFlocks: number;
  birdsAlive: number;
  lostLast7: number;
}

interface Attention {
  key: string;
  text: string;
  /** What the problem is about, shown as its icon. */
  kind: IconKind;
  /** The Farms & Sheds tree node to open when it is clicked. */
  node: string;
}

const MAX_ATTENTION = 15;

/** Yesterday as YYYY-MM-DD: the day the register should already cover. */
function yesterdayIso(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

/**
 * Whether a flock's register reaches a given day.
 *
 * A flock placed today has no day to miss yet, so it is never chased for one.
 */
function hasEntryFor(flock: Flock, day: string): boolean {
  if (String(flock.placementDate).slice(0, 10) > day) return true;
  if (!flock.lastEntryOn) return false;
  return String(flock.lastEntryOn).slice(0, 10) >= day;
}

/** Opens a place in the Farms & Sheds workbench, which reads its node on arrival. */
function openInWorkbench(node: string) {
  writeStored('poultry.farmManager.node', node);
  writeStored('poultry.farmManager.view', 'grid');
  useShellNav.getState().navigate('mdm-farm');
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

/**
 * Farm Manager's front page: how much there is, how big it is, what is in it,
 * and what is missing. Follows the farms chosen in the top bar, like every
 * other screen.
 *
 * Bird numbers come from the live flocks and the daily register behind them,
 * so a farm between cycles reads as empty rather than showing a last figure
 * that is no longer true.
 */
export function FarmDashboard() {
  const { can } = useAuth();
  const farmIds = useFarmScope((s) => s.selectedFarmIds);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const farms = useQuery({ queryKey: ['farms', 'list', false], queryFn: () => api.get('/api/v1/farms') });
  const tree = useQuery({ queryKey: ['farms', 'hierarchy', false], queryFn: () => api.get('/api/v1/farms/hierarchy') });
  const sheds = useQuery({
    queryKey: ['sheds', 'all'],
    queryFn: () => api.get('/api/v1/sheds'),
    enabled: can('mdm.shed.read'),
  });
  const flocks = useQuery({
    queryKey: ['flocks', 'list', 'live-dashboard'],
    // Every live flock, across every farm the caller may see; narrowing to
    // the top bar's farms happens below alongside everything else.
    queryFn: () => api.get('/api/v1/flocks', { status: 'live', take: 200, skip: 0 }),
    enabled: can('mdm.flock.read'),
  });

  const treeById = new Map(((tree.data ?? []) as HierarchyFarm[]).map((f) => [f.id, f]));
  const shedList = (sheds.data ?? []) as Shed[];
  const flockList = (flocks.data?.items ?? []) as Flock[];

  const summaries: FarmSummary[] = ((farms.data ?? []) as Farm[])
    .filter((f) => farmIds.length === 0 || farmIds.includes(f.id))
    .map((f) => {
      const node = treeById.get(f.id);
      const farmSheds = shedList.filter((s) => s.farmId === f.id);
      const farmFlocks = flockList.filter((fl) => fl.farmId === f.id);
      return {
        liveFlocks: farmFlocks.length,
        birdsAlive: farmFlocks.reduce((n, fl) => n + fl.birdsAlive, 0),
        lostLast7: farmFlocks.reduce((n, fl) => n + fl.lostLast7, 0),
        id: f.id,
        code: f.code,
        nameEn: f.nameEn,
        district: f.district,
        isActive: f.isActive,
        sheds: f.shedCount,
        sections: node ? node.sheds.reduce((n, s) => n + s.sections.length, 0) : 0,
        pens: node ? node.sheds.reduce((n, s) => n + s.sections.reduce((m, se) => m + se.pens.length, 0), 0) : 0,
        capacity: farmSheds.reduce((n, s) => n + (s.capacity ?? 0), 0),
        stores: f.storeCount,
        flocks: f.flockCount,
      };
    });

  const totals = summaries.reduce(
    (t, f) => ({
      sheds: t.sheds + f.sheds,
      sections: t.sections + f.sections,
      pens: t.pens + f.pens,
      capacity: t.capacity + f.capacity,
      birdsAlive: t.birdsAlive + f.birdsAlive,
      lostLast7: t.lostLast7 + f.lostLast7,
      liveFlocks: t.liveFlocks + f.liveFlocks,
    }),
    { sheds: 0, sections: 0, pens: 0, capacity: 0, birdsAlive: 0, lostLast7: 0, liveFlocks: 0 },
  );

  const yesterday = yesterdayIso();
  const yesterdayLabel = shortDate(yesterday);
  const inScope = new Set(summaries.map((f) => f.id));
  const shedsInScope = shedList.filter((s) => inScope.has(s.farmId));
  const noCapacity = shedsInScope.filter((s) => s.capacity === null || s.capacity === 0);

  const attention: Attention[] = [
    ...summaries
      .filter((f) => f.sheds === 0)
      .map((f) => ({ key: `nosheds-${f.id}`, kind: 'shed' as const, text: `${f.code} · ${f.nameEn} has no sheds yet.`, node: `farm:${f.id}` })),
    ...summaries
      .filter((f) => f.stores === 0)
      .map((f) => ({
        key: `nostore-${f.id}`,
        kind: 'store' as const,
        text: `${f.code} · ${f.nameEn} has no store, so stock cannot be received there.`,
        node: `farm:${f.id}`,
      })),
    ...noCapacity.map((s) => ({
      key: `nocap-${s.id}`,
      kind: 'capacity' as const,
      text: `Shed ${s.code} · ${s.nameEn} on ${s.farmCode} has no bird capacity set.`,
      node: `farm:${s.farmId}`,
    })),
    // A live flock with nothing entered for yesterday means a day of deaths
    // and feed nobody has written down — the one thing on this screen that
    // gets worse the longer it is left.
    ...flockList
      .filter((f) => inScope.has(f.farmId) && !hasEntryFor(f, yesterday))
      .map((f) => ({
        key: `noentry-${f.id}`,
        kind: 'attention' as const,
        text: `${f.code} on ${f.farmCode} has no register entry for ${yesterdayLabel}.`,
        node: `farm:${f.farmId}`,
      })),
  ];

  const columns: Array<GridColumn<FarmSummary>> = [
    { key: 'code', header: 'Code', width: 110, render: (f) => <span className="mono">{f.code}</span> },
    {
      key: 'name',
      header: 'Farm',
      width: 220,
      render: (f) => (
        <span className="withicon">
          <AppIcon kind="farm" />
          {f.nameEn}
        </span>
      ),
    },
    { key: 'district', header: 'District', width: 130, render: (f) => <span className="dim">{f.district ?? '—'}</span> },
    { key: 'sheds', header: 'Sheds', align: 'end', width: 80, render: (f) => f.sheds },
    { key: 'sections', header: 'Sections', align: 'end', width: 90, render: (f) => f.sections },
    { key: 'pens', header: 'Pens', align: 'end', width: 70, render: (f) => f.pens },
    { key: 'capacity', header: 'Bird capacity', align: 'end', width: 120, render: (f) => (f.capacity ? formatCount(f.capacity) : '—') },
    ...(can('mdm.flock.read')
      ? [
          {
            key: 'liveFlocks',
            header: 'Live flocks',
            align: 'end' as const,
            width: 100,
            render: (f: FarmSummary) => f.liveFlocks,
          },
          {
            key: 'birdsAlive',
            header: 'Birds alive',
            align: 'end' as const,
            width: 110,
            render: (f: FarmSummary) => (f.liveFlocks === 0 ? <span className="dim">—</span> : formatCount(f.birdsAlive)),
          },
        ]
      : []),
    { key: 'stores', header: 'Stores', align: 'end', width: 80, render: (f) => f.stores },
  ];

  const error = farms.error ?? tree.error ?? sheds.error ?? flocks.error;
  const viewingLabel =
    farmIds.length === 0
      ? 'All farms'
      : summaries.length === 1
        ? `Viewing ${summaries[0]!.code} · ${summaries[0]!.nameEn}`
        : `Viewing ${summaries.length} farms`;

  return (
    <div className="workbench workbench--cards">
      <div className="workbench__tiles">
        <span className="worktile">
          <span className="worktile__label withicon">
            <AppIcon kind="farm" size={14} />
            Farms
          </span>
          <strong>{summaries.length}</strong>
        </span>
        <span className="worktile">
          <span className="worktile__label withicon">
            <AppIcon kind="shed" size={14} />
            Sheds
          </span>
          <strong>{totals.sheds}</strong>
        </span>
        <span className="worktile">
          <span className="worktile__label withicon">
            <AppIcon kind="section" size={14} />
            Sections · Pens
          </span>
          <strong>
            {totals.sections} · {totals.pens}
          </strong>
        </span>
        {can('mdm.shed.read') && (
          <span className={`worktile${noCapacity.length > 0 ? ' worktile--warn' : ''}`}>
            <span className="worktile__label withicon">
            <AppIcon kind="capacity" size={14} />
            Bird capacity
          </span>
            <strong>{formatCount(totals.capacity)}</strong>
          </span>
        )}
        {can('mdm.flock.read') && (
          <>
            <span className="worktile">
              <span className="worktile__label withicon">
                <AppIcon kind="flock" size={14} />
                Birds alive
              </span>
              <strong>{formatCount(totals.birdsAlive)}</strong>
            </span>
            <span className="worktile">
              <span className="worktile__label withicon">
                <AppIcon kind="attention" size={14} />
                Lost in 7 days
              </span>
              <strong>{formatCount(totals.lostLast7)}</strong>
            </span>
          </>
        )}
        <span className={`worktile${attention.length > 0 ? ' worktile--alert' : ''}`}>
          <span className="worktile__label withicon">
            <AppIcon kind="attention" size={14} />
            Needs attention
          </span>
          <strong>{attention.length}</strong>
        </span>
      </div>

      {error && <ErrorNote message={error.message} />}

      <SplitView
        id="farm-dashboard"
        initial={760}
        firstLabel="farm list"
        first={
          <div className="workbench__main">
            <DataGrid
              id="farm-dashboard"
              label="Farms at a glance"
              columns={columns}
              rows={summaries}
              rowKey={(f) => f.id}
              selectedKey={selectedKey}
              onSelect={setSelectedKey}
              onOpen={(f) => openInWorkbench(`farm:${f.id}`)}
              actions={(f) => [{ label: 'Open in Farms & Sheds', shortcut: 'Enter', onSelect: () => openInWorkbench(`farm:${f.id}`) }]}
              loading={farms.isPending}
              empty="No farms yet. Add one in Farms & Sheds."
            />
          </div>
        }
        second={
          <DetailPane
            tabs={[
              {
                id: 'attention',
                label: 'Needs attention',
                content:
                  attention.length === 0 ? (
                    <p className="muted">
                      Nothing needs attention. Every farm has sheds and a store, every shed has a capacity, and every
                      live flock&rsquo;s register is up to date.
                    </p>
                  ) : (
                    <ul className="attentionlist">
                      {attention.slice(0, MAX_ATTENTION).map((a) => (
                        <li key={a.key}>
                          <button type="button" className="linkbutton withicon" onClick={() => openInWorkbench(a.node)}>
                            <AppIcon kind={a.kind} />
                            {a.text}
                          </button>
                        </li>
                      ))}
                      {attention.length > MAX_ATTENTION && (
                        <li className="muted">and {attention.length - MAX_ATTENTION} more</li>
                      )}
                    </ul>
                  ),
              },
            ]}
            activeTab="attention"
            onTabChange={() => {}}
          />
        }
      />

      <StatusBar>
        <span>{viewingLabel}</span>
        <span>{plural(summaries.length, 'farm')}</span>
        {can('mdm.flock.read') && <span>{plural(totals.liveFlocks, 'live flock')}</span>}
      </StatusBar>
    </div>
  );
}
